import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  query,
  limit,
  getDoc
} from 'firebase/firestore';
import { firestore } from '../firebase/config';
import { ProjectCategory, ServiceCategory } from '../types';
import { initializeOwnerAccount } from './userManagementService';

/**
 * Initializes the Firebase database with default collections and documents if they don't exist
 * This allows the app to work immediately without manual setup in Firebase Console
 */
export async function initializeDatabase() {
  try {
    console.log('Checking database initialization...');
    
    // Set your email as the owner account
    await initializeOwnerAccount('Cody@Paragonlivedesign.com');
    
    // Initialize team collection if empty
    await initializeTeamCollection();
    
    // Initialize pages collection if empty
    await initializePagesCollection();
    
    // Initialize projects collection if empty
    await initializeProjectsCollection();

    // Initialize services collection if empty
    await initializeServicesCollection();
    
    console.log('Database initialization complete');
  } catch (error) {
    console.error('Error initializing database:', error);
    throw error;
  }
}

/**
 * Initializes the team collection with default members if empty
 */
async function initializeTeamCollection() {
  // Check if team collection has any documents
  const teamQuery = query(collection(firestore, 'team'), limit(1));
  const teamSnapshot = await getDocs(teamQuery);
  
  // If empty, populate with initial data
  if (teamSnapshot.empty) {
    console.log('Initializing team collection with default data');
    
    // Add sample team members
    await setDoc(doc(firestore, 'team', 'member1'), {
      name: 'John Smith',
      position: 'CEO & Founder',
      bio: 'John has over 15 years of experience in the audio-visual industry and founded Paragon Live Design in 2010.',
      imageUrl: '/images/team/placeholder.jpg',
      order: 1
    });
    
    await setDoc(doc(firestore, 'team', 'member2'), {
      name: 'Sarah Johnson',
      position: 'Creative Director',
      bio: 'Sarah brings her artistic vision to every project, ensuring each event has a unique and memorable design.',
      imageUrl: '/images/team/placeholder.jpg',
      order: 2
    });
    
    await setDoc(doc(firestore, 'team', 'member3'), {
      name: 'Michael Chen',
      position: 'Technical Director',
      bio: 'Michael oversees all technical aspects of our productions, ensuring flawless execution of complex setups.',
      imageUrl: '/images/team/placeholder.jpg',
      order: 3
    });
  } else {
    console.log('Team collection already initialized');
  }
}

/**
 * Initializes the pages collection with default content if empty
 */
async function initializePagesCollection() {
  // Check if pages collection exists and has necessary documents
  const homeDocRef = doc(firestore, 'pages', 'home');
  const aboutDocRef = doc(firestore, 'pages', 'about');
  
  const homeDoc = await getDoc(homeDocRef);
  const aboutDoc = await getDoc(aboutDocRef);
  
  // Initialize home page if needed
  if (!homeDoc.exists()) {
    console.log('Initializing home page with default content');
    
    await setDoc(homeDocRef, {
      heroTitle: 'Welcome to Paragon Live Design',
      heroSubtitle: 'Creating Exceptional Event Experiences',
      heroDescription: 'We transform spaces into unforgettable experiences with innovative design, cutting-edge technology, and flawless execution.',
      servicesTitle: 'Our Services',
      servicesSubtitle: 'Comprehensive solutions for your events',
      aboutSectionTitle: 'About Us',
      aboutSectionDescription: 'Paragon Live Design is a premier event production company specializing in creating immersive experiences through innovative design and cutting-edge technology.',
      clientsTitle: 'Our Clients',
      featuredProjects: [
        {
          title: 'Corporate Gala',
          description: 'Annual awards ceremony with custom stage design and immersive lighting',
          imageUrl: '/images/projects/placeholder1.jpg'
        },
        {
          title: 'Music Festival',
          description: 'Large-scale outdoor festival with multiple stages and synchronized lighting',
          imageUrl: '/images/projects/placeholder2.jpg'
        },
        {
          title: 'Product Launch',
          description: 'High-profile product unveiling with interactive displays and custom content',
          imageUrl: '/images/projects/placeholder3.jpg'
        }
      ]
    });
  }
  
  // Initialize about page if needed
  if (!aboutDoc.exists()) {
    console.log('Initializing about page with default content');
    
    await setDoc(aboutDocRef, {
      pageTitle: 'About Paragon Live Design',
      mainContent: 'Founded in 2010, Paragon Live Design has established itself as a leader in event production and design. Our team of creative professionals and technical experts work together to create immersive experiences that exceed our clients\' expectations.',
      mission: 'Our mission is to transform ordinary events into extraordinary experiences through innovative design, cutting-edge technology, and flawless execution.',
      vision: 'We envision a world where every event is an opportunity to create meaningful connections and lasting memories.',
      historyTitle: 'Our History',
      historyContent: 'Paragon Live Design began as a small team of passionate event professionals. Over the years, we have grown to become a full-service production company serving clients nationwide.',
      valuesTitle: 'Our Values',
      values: [
        {
          title: 'Innovation',
          description: 'We constantly explore new technologies and creative approaches to push the boundaries of what\'s possible.'
        },
        {
          title: 'Excellence',
          description: 'We are committed to delivering the highest quality in every aspect of our work.'
        },
        {
          title: 'Collaboration',
          description: 'We believe in the power of teamwork and partnership with our clients to achieve remarkable results.'
        }
      ]
    });
  }
}

/**
 * Initializes the projects collection with default projects if empty
 * Uses a flag in the settings collection to prevent re-initialization after deliberate deletion
 */
async function initializeProjectsCollection() {
  // First check if we've already initialized projects before
  const initFlagRef = doc(firestore, 'settings', 'initialization');
  const initFlagDoc = await getDoc(initFlagRef);
  
  // If we have a flag indicating projects were initialized, don't reinitialize
  if (initFlagDoc.exists() && initFlagDoc.data().projectsInitialized) {
    console.log('Projects were previously initialized, skipping...');
    return;
  }
  
  // Check if projects collection has any documents
  const projectsQuery = query(collection(firestore, 'projects'), limit(1));
  const projectsSnapshot = await getDocs(projectsQuery);
  
  // If empty, populate with initial data
  if (projectsSnapshot.empty) {
    console.log('Initializing projects collection with default data');
    
    // Add sample projects
    await setDoc(doc(firestore, 'projects', 'project1'), {
      title: 'Concert Stage Design',
      slug: 'concert-stage-design',
      client: 'Music Festival Productions',
      description: 'Custom stage design for a major music festival featuring LED panels and dynamic lighting. The design incorporated interactive elements that responded to the music and created an immersive experience for attendees.',
      coverImage: 'https://source.unsplash.com/random/800x600/?concert',
      images: [
        'https://source.unsplash.com/random/800x600/?concert',
        'https://source.unsplash.com/random/800x600/?stage',
        'https://source.unsplash.com/random/800x600/?festival'
      ],
      category: [ProjectCategory.STAGE_DESIGN.toString(), ProjectCategory.PRODUCTION_DESIGN.toString()],
      featured: true,
      completionDate: new Date(2023, 5, 15), // June 15, 2023
      designer: 'Sarah Johnson',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    await setDoc(doc(firestore, 'projects', 'project2'), {
      title: 'Corporate Event Layout',
      slug: 'corporate-event-layout',
      client: 'Tech Innovations Inc.',
      description: 'Comprehensive event layout and production design for an annual corporate conference. The design included multiple presentation stages, networking areas, and interactive product demonstration zones.',
      coverImage: 'https://source.unsplash.com/random/800x600/?corporate',
      images: [
        'https://source.unsplash.com/random/800x600/?corporate',
        'https://source.unsplash.com/random/800x600/?conference',
        'https://source.unsplash.com/random/800x600/?business'
      ],
      category: [ProjectCategory.EVENT_PLANNING.toString(), ProjectCategory.CONSULTING.toString()],
      featured: false,
      completionDate: new Date(2023, 3, 10), // April 10, 2023
      designer: 'John Smith',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    await setDoc(doc(firestore, 'projects', 'project3'), {
      title: 'Theater Production Design',
      slug: 'theater-production-design',
      client: 'Metropolitan Theater Company',
      description: 'Set and lighting design for a Broadway-style theatrical production. The design created a versatile environment that could transform between multiple scenes with minimal set changes.',
      coverImage: 'https://source.unsplash.com/random/800x600/?theater',
      images: [
        'https://source.unsplash.com/random/800x600/?theater',
        'https://source.unsplash.com/random/800x600/?stage',
        'https://source.unsplash.com/random/800x600/?performance'
      ],
      category: [ProjectCategory.PRODUCTION_DESIGN.toString(), ProjectCategory.DRAFTING.toString()],
      featured: true,
      completionDate: new Date(2022, 10, 5), // November 5, 2022
      designer: 'Michael Chen',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    await setDoc(doc(firestore, 'projects', 'project4'), {
      title: 'Television Studio Layout',
      slug: 'television-studio-layout',
      client: 'Global Broadcasting Network',
      description: 'Complete studio redesign for a national television network. The layout optimized camera positions, lighting, and audience seating while maintaining flexibility for different show formats.',
      coverImage: 'https://source.unsplash.com/random/800x600/?television',
      images: [
        'https://source.unsplash.com/random/800x600/?television',
        'https://source.unsplash.com/random/800x600/?studio',
        'https://source.unsplash.com/random/800x600/?broadcast'
      ],
      category: [ProjectCategory.STUDIO_DESIGN.toString(), ProjectCategory.DRAFTING.toString()],
      featured: false,
      completionDate: new Date(2022, 7, 20), // August 20, 2022
      designer: 'Sarah Johnson',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    // Set the initialization flag to prevent future re-initialization
    await setDoc(initFlagRef, {
      projectsInitialized: true,
      timestamp: new Date()
    });
  } else {
    console.log('Projects collection already initialized');
    
    // Even if projects exist, set the flag to prevent future re-initialization
    await setDoc(initFlagRef, {
      projectsInitialized: true,
      timestamp: new Date()
    });
  }
}

/**
 * Initializes the services collection with default services if empty
 */
async function initializeServicesCollection() {
  // Check if services collection has any documents
  const servicesQuery = query(collection(firestore, 'services'), limit(1));
  const servicesSnapshot = await getDocs(servicesQuery);
  
  // If empty, populate with initial data
  if (servicesSnapshot.empty) {
    console.log('Initializing services collection with default data');
    
    // Add sample services
    await setDoc(doc(firestore, 'services', 'service1'), {
      title: 'Production Design',
      slug: 'production-design',
      description: 'Comprehensive production design services for events, concerts, theater, and more. Our team of experienced designers will transform your vision into a stunning reality.',
      icon: 'DesignServices',
      features: [
        'Custom stage and set design',
        'Lighting design and programming',
        'Visual effects planning',
        'Technical drawings and specifications',
        'Budget management and optimization'
      ],
      category: ServiceCategory.PRODUCTION_DESIGN,
      images: [],
      priceRange: '$2,500 - $25,000',
      turnaroundTime: '3-6 weeks',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    await setDoc(doc(firestore, 'services', 'service2'), {
      title: 'Consulting',
      slug: 'consulting',
      description: 'Expert consulting services to help you plan and execute your production vision. We provide guidance on technical requirements, vendor selection, and overall production strategy.',
      icon: 'SupportAgent',
      features: [
        'Production feasibility assessment',
        'Technical requirements analysis',
        'Vendor selection and management',
        'Risk assessment and mitigation',
        'On-site technical direction'
      ],
      category: ServiceCategory.CONSULTING,
      images: [],
      priceRange: '$1,500 - $10,000',
      turnaroundTime: '1-4 weeks',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    await setDoc(doc(firestore, 'services', 'service3'), {
      title: 'Drafting',
      slug: 'drafting',
      description: 'Precise technical drafting services for production design and planning. Our CAD specialists create detailed drawings to bring your ideas to life and ensure smooth execution.',
      icon: 'Architecture',
      features: [
        '2D CAD drawings',
        '3D modeling and visualization',
        'Technical specifications',
        'Construction documents',
        'As-built documentation'
      ],
      category: ServiceCategory.DRAFTING,
      images: [],
      priceRange: '$800 - $5,000',
      turnaroundTime: '1-3 weeks',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    await setDoc(doc(firestore, 'services', 'service4'), {
      title: 'Production Management',
      slug: 'production-management',
      description: 'End-to-end production management services to ensure your event runs smoothly. We handle all aspects of production coordination, from pre-planning to on-site execution.',
      icon: 'ManageAccounts',
      features: [
        'Timeline development and tracking',
        'Budget management',
        'Crew coordination',
        'Logistics planning',
        'On-site supervision'
      ],
      category: ServiceCategory.PRODUCTION_MANAGEMENT,
      images: [],
      priceRange: '$3,000 - $20,000',
      turnaroundTime: '2-8 weeks',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    await setDoc(doc(firestore, 'services', 'service5'), {
      title: 'Equipment Rental',
      slug: 'equipment-rental',
      description: 'High-quality equipment rental for all your production needs. From lighting and audio to staging and special effects, we have everything you need for a successful event.',
      icon: 'Inventory',
      features: [
        'Lighting equipment',
        'Audio systems',
        'Video and projection',
        'Staging and rigging',
        'Special effects equipment'
      ],
      category: ServiceCategory.EQUIPMENT_RENTAL,
      images: [],
      priceRange: 'Starting at $500',
      turnaroundTime: 'Same day - 1 week',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    console.log('Services collection initialized with default data');
  } else {
    console.log('Services collection already initialized');
  }
} 